import { KeyValue } from '@angular/common';
import { Injectable } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { NO_ORDER, PERFECTION_ONLY } from 'app/_components/back-counter-request-tab/back-counter-request-tab.utils';
import { OrderConfirmationItem } from 'entities/index';
import { ProcurementGroup, SupplierCheckState } from 'entities/procurement-group';
import { ProcurementType } from 'entities/procurement-type';
import { CreatePurchaseOrdersBatchRequest, CreatePurchaseOrdersBatchResult, PurchaseOrderDeliveryType, PurchaseOrderItem } from 'entities/purchase-order';
import { HotFlagRadioButtonType } from 'entities/purchase-order/hot-flag';
import { ToastType } from 'entities/toast-type';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { LoggerService } from 'services/logger.service';
import { OrderService } from 'services/order.service';
import { PurchaseOrderService } from 'services/purchase-order.service';
import { ToastService } from 'services/toast.service';
import { OpeningFrom, RequestOrderItem } from 'store/back-counter/back-counter.entities';

export interface AssignItemsVendor {
  vendorNumber: string;
  checkedItems: (OrderConfirmationItem & SupplierCheckState)[];
}

export interface UnassignedState {
  checked: boolean;
  unassignedItem: (OrderConfirmationItem & SupplierCheckState)[];
}

export interface ProcurementGroupState {
  info: ProcurementGroup;
  state: {
      deliveryType: PurchaseOrderDeliveryType;
      showMore: boolean;
      orderItemNumber?: string;
      createRequestOnly?: HotFlagRadioButtonType;
      supplier?: string;
      sameAddress?: boolean;
  };
  orderItems: (OrderConfirmationItem & SupplierCheckState)[];
  hasMore: boolean;
  moreCount: number;
}

export interface PurchaseOrderCreateModalComponentState {
  loading: boolean;
  orderNumber: string;
  sourceOrderNo: string;
  branchCode: string;
  procurementGroups: ProcurementGroup[];
  groupStates: {
    deliveryType: PurchaseOrderDeliveryType;
    showMore: boolean;
    orderItemNumber?: string;
    createRequestOnly?: HotFlagRadioButtonType;
    supplier?: string;
    sameAddress?: boolean;
  }[];
  submitting: boolean;
  openingFrom?: OpeningFrom;
}

const initialState: PurchaseOrderCreateModalComponentState = {
  loading: false,
  orderNumber: null,
  sourceOrderNo: null,
  branchCode: null,
  procurementGroups: [],
  groupStates: [],
  submitting: false
}

const purchaseRequisitionApprovalMessage = 'PR was created and may need further approval';

interface MessageResponse {
  message?: unknown;
  Message?: unknown;
  errorMessage?: unknown;
  ErrorMessage?: unknown;
  messages?: unknown;
  Messages?: unknown;
}

@Injectable()
export class PurchaseOrderCreateModalComponentStore extends ComponentStore<PurchaseOrderCreateModalComponentState> {
  constructor(
    private readonly orderService: OrderService,
    private readonly purchaseOrderService: PurchaseOrderService,
    private readonly activeModal: NgbActiveModal,
    private readonly toastService: ToastService,
    private readonly logger: LoggerService
  ) {
    super(initialState);
  }

  // Actions/Effects
  public readonly getProcurementGroups = this.effect((params$: Observable<{orderNumber: string, branchCode: string}>) =>
    params$.pipe(
      tap((params) => this.patchState({ loading: true, orderNumber: params.orderNumber, branchCode: params.branchCode })),
      switchMap((params) =>
        this.orderService.getProcurementGroups(params.orderNumber, params.branchCode)
          .pipe(
            map((procGroups) => procGroups.filter(x => x.orderItems != null)),
            tapResponse({
              next: (procurementGroups) => this.patchState({
                loading: false,
                procurementGroups,
                groupStates: procurementGroups.map((group) => {
                  return ({
                    deliveryType: null,
                    showMore: false,
                    supplier: "",
                    sameAddress: null
                  });
                })
              }),
              error: (error) => {
                this.logger.error('getProcurementGroups error', error);
                if (Array.isArray(error)) {
                  const toasts = error
                    .map((message) => ({ message, type: ToastType.Error }));
                  this.toastService.showMultilineToast(toasts);
                }
                this.patchState({ loading: false });
              }
            })
          )
      )
    )
  );

  public readonly getProcurementGroupsByRequestOrderItem = this.effect((params$: Observable<{
    orderNumber: string,
    sourceOrderNo: string,
    branchCode: string,
    requestOrderItems: RequestOrderItem[]
  }>) =>
    params$.pipe(
      tap((params) => this.patchState({ loading: true, orderNumber: params.orderNumber, sourceOrderNo: params.sourceOrderNo, branchCode: params.branchCode })),
      switchMap((params) =>
      {
        return this.orderService.getBackCounterProcurementGroups(params.orderNumber, params.branchCode)
          .pipe(
            tapResponse({
              next: (resp) => {
                const procurementGroups = resp
                  .map(group => ({
                    ...group,
                    orderItems: group.orderItems?.filter(orderItem => params.requestOrderItems.some(reqItem => +reqItem.itemNumber === orderItem.itemNumber))
                  }))
                  .filter(group => group.orderItems?.length > 0);
                const newProcurementGroups = this.getProcurementGroupFromRequestOrderItems(params.requestOrderItems, params.orderNumber, procurementGroups);
                return this.patchState({
                  loading: false,
                  openingFrom: 'BCT',
                  procurementGroups: newProcurementGroups,
                  groupStates: newProcurementGroups.map((group) => {
                    return ({
                      deliveryType: null,
                      showMore: false,
                      supplier: "",
                      sameAddress: null
                    });
                  })
                });
              },
              error: (error) => {
                this.logger.error('getProcurementGroups error', error);
                if (Array.isArray(error)) {
                  const toasts = error
                    .map((message) => ({ message, type: ToastType.Error }));
                  this.toastService.showMultilineToast(toasts);
                }
                this.patchState({ loading: false });
              }
            })
          )
      }
      )
    )
  );

  public readonly submit = this.effect((source$) =>
    source$.pipe(
      tap(() => this.patchState({ submitting: true })),
      map(() => this.get<CreatePurchaseOrdersBatchRequest>((state) => ({
        orderNumber: state.openingFrom === 'BCT' ? state.sourceOrderNo : state.orderNumber,
        items: state.groupStates
          .map((groupState, index) => ({ ...state.procurementGroups[index], ...groupState }))
          .map(({ procurementType, vendorNumber, deliveryType, orderItems, createRequestOnly, supplier }) => ({
            procurementType,
            vendorNumber: this.getVendorNumber(vendorNumber ?? (orderItems?.[0] as any)?.vendor ),
            purchaseOrderDeliveryType: deliveryType ?? 'other',
            purchaseOrderItems: orderItems.map((orderItem) => {
              const result = {
                orderItemNumber: state.openingFrom === 'BCT' ? ""+orderItem.roItemno : ""+orderItem.itemNumber,
                supplier: orderItem.supplier ?? supplier,
                createRequestOnly: createRequestOnly === 'requisition' ? "X": "",
              } as PurchaseOrderItem;
              return result;
            })
          }))
      }))),
      switchMap((batchRequest) => {
        return this.purchaseOrderService.createPurchaseOrdersBatch(batchRequest)
          .pipe(
            tapResponse({
              next: (batchResult) => {
                batchResult.items
                  ?.filter((item) => item.errorMessage)
                  ?.forEach((item) => this.toastService.showToast(item.errorMessage, ToastType.Success));
                this.activeModal.close(batchResult);
              },
              error: (error) => {
                this.logger.error('createPurchaseOrdersBatch error', error);
                const approvalMessage = this.getPurchaseRequisitionApprovalMessage(error);
                if (approvalMessage) {
                  this.activeModal.close(this.createPurchaseRequisitionApprovalResult(approvalMessage));
                  return;
                }

                if (Array.isArray(error)) {
                  const toasts = error
                    .map((message) => ({ message, type: ToastType.Error }));
                  this.toastService.showMultilineToast(toasts);
                }
                else {
                  const errorMessage = (error as any)?.message;
                  const message = errorMessage && errorMessage !== undefined && errorMessage != null && errorMessage !== ''
                    ? errorMessage
                    : 'An unexpected error occurred during creating purchase order.';
                  this.toastService.showToast(message, ToastType.Error);
                }
                this.patchState({ submitting: false });
              }
            })
          )
      }
      )
    )
  );

  // Actions/Reducers
  public readonly showMore = this.updater((state, index: number) => ({
    ...state,
    groupStates: state.groupStates
      .map((groupState, groupIndex) =>
        groupIndex === index
        ? { ...groupState, showMore: true }
        : groupState
      )
  }));

  public readonly showLess = this.updater((state, index: number) => ({
    ...state,
    groupStates: state.groupStates
      .map((groupState, groupIndex) =>
        groupIndex === index
        ? { ...groupState, showMore: false }
        : groupState
      )
  }));

  public readonly updateDeliveryType = this.updater((state, { key: index, value: deliveryType }: KeyValue<number, PurchaseOrderDeliveryType>) => ({
    ...state,
    groupStates: state.groupStates
      .map((groupState, groupIndex) =>
        groupIndex === index
        ? { ...groupState, deliveryType }
        : groupState
      )
  }));

  public readonly updateSameAddress = this.updater((state, { key: index, value: sameAddress }: KeyValue<number, boolean>) => ({
    ...state,
    groupStates: state.groupStates
      .map((groupState, groupIndex) =>
        groupIndex === index
        ? { ...groupState, sameAddress }
        : groupState
      )
  }));

  public readonly updateSupplier = this.updater((state, { key: index, value: supplier }: KeyValue<number, string>) => {
    if (supplier === "null") {
      supplier = null;
    }
    return ({
      ...state,
      groupStates: state.groupStates
        .map((groupState, groupIndex) =>
          groupIndex === index
          ? { ...groupState, supplier }
          : groupState
        )
    });
  });

  public readonly updateHotFlagItems = this.updater((state, { key: index, value: hotFlagType }: KeyValue<number, HotFlagRadioButtonType>) => ({
    ...state,
    groupStates: state.groupStates
      .map((groupState, groupStateIndex) =>
      groupStateIndex === index
        ? { ...groupState, createRequestOnly: hotFlagType }
        : groupState
      )
  }));

  public readonly resetCheckItems = this.updater((state) => ({
    ...state,
    procurementGroups: [...state.procurementGroups.map(m => ({...m, orderItems: m.orderItems.map(m => ({...m, checked: false}))}))]
  }));

  public readonly setSupplier = this.updater((state, {vendorNumber, filteredOrderItems}: any) => ({
    ...state,
    procurementGroups: [...state.procurementGroups.map(m => ({...m,
      orderItems: m.orderItems.map(m => ({
        ...m,
        supplier: filteredOrderItems.find(f => {
          if (state.openingFrom === 'BCT') {
            return f._id === m._id;
          }
          else {
            return f.cartItemId === m.cartItemId
          }
        }) ? vendorNumber : (m?.supplier ?? ''),
        checked: false
      }))}))]
  }));

  public readonly setCheckedItemsWithSupplier = this.updater(
    (state, { vendorNumber, checkedItems }: AssignItemsVendor) => ({
      ...state,
      procurementGroups: [
        ...state.procurementGroups.map((m) => ({
          ...m,
          orderItems: m.orderItems.map((m) => ({
            ...m,
            supplier: checkedItems.find((f) => {
              if (state.openingFrom === 'BCT') {
                const hasBctItemId = f._id === m._id;
                return hasBctItemId;
              } else {
                const hasCartItemId = f.cartItemId === m.cartItemId;
                return hasCartItemId;
              }
            })
              ? vendorNumber
              : m?.supplier ?? '',
            checked: false,
          })),
        })),
      ],
    }),
  );

  public readonly selectAll = this.updater((state, {checked, supplier}: SupplierCheckState) => ({
      ...state,
      procurementGroups: [
        ...state.procurementGroups.map(m => ({
          ...m,
          orderItems: m.orderItems.map(m => ({
            ...m,
            checked: supplier && supplier === m.supplier ? checked : false
          }))
        }))
      ]
  }));

  public readonly selectAllUnassigned = this.updater((state, {checked, unassignedItem}: UnassignedState) => ({
    ...state,
    procurementGroups: [...state.procurementGroups.map(m => ({
      ...m,
      orderItems: m.orderItems.map(m => ({
        ...m,
        checked: unassignedItem.find(f => f.cartItemId === m.cartItemId) ? checked : this.isCheckedByItem(m, checked)
      }))
    }))]
}));

  // Selectors
  public readonly busy$ = this.select((state) => state.loading || state.submitting);

  public readonly groups$ = this.select((state) =>
    state.groupStates
      .map((groupState, index) => ({ info: state.procurementGroups[index], groupState }))
      .filter(({ info }) => info && info.orderItems) // Only include valid groups with orderItems
      .map(({ info, groupState }) => ({
        info,
        state: groupState,
        orderItems: groupState.showMore
          ? info.orderItems
          : info.orderItems.slice(0, 3),
        hasMore: info.orderItems.length > 3,
        moreCount: info.orderItems.length - 3
      } as ProcurementGroupState))
  );

  isCheckedByItem(item: OrderConfirmationItem & SupplierCheckState, checked: boolean): boolean {
    return item?.checked ?? checked
  }

  getProcurementType(m: RequestOrderItem): ProcurementType {
    if(m.hotflag) return 'hotflag';
    if(m.buyout) return 'buyout';
    if(m.sto) return 'sto';
    return 'other';
  }

  getProcurementGroupFromRequestOrderItems(
    requestOrderItems: RequestOrderItem[],
    orderNumber: string,
    procurementGroups: ProcurementGroup[]
  ): ProcurementGroup[] {
    // Group items by procurement type and vendor to create separate groups for each unique vendor
    const groupMap = new Map<
      string,
      {
        procurementType: ProcurementType;
        vendorNumber: string;
        orderItems: any[];
      }
    >();

    for (const item of requestOrderItems) {
      const procurementType = this.getProcurementType(item);
      const vendorNumber = String(item.purchaseVendor || "");
      const groupKey = `${procurementType}-${vendorNumber}`;

      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, {
          procurementType,
          vendorNumber,
          orderItems: [],
        });
      }

      groupMap.get(groupKey).orderItems.push(this.toBase(item));
    }

    // Convert map to procurement groups array
    const groups: ProcurementGroup[] = [];
    for (const [, group] of groupMap) {
      const separatedHotFlags = group.orderItems.every(
        (e) => PERFECTION_ONLY === e.hotflag || NO_ORDER === e.hotflag
      );
      if (separatedHotFlags) {
        groups.push(
          ...group.orderItems.map((item, _) =>
            this.createProcurementGroup(
              orderNumber,
              {
                vendorNumber: group.vendorNumber,
                procurementType: group.procurementType,
                orderItems: [item],
              },
              procurementGroups
            )
          )
        );
      } else {
        groups.push(
          this.createProcurementGroup(orderNumber, group, procurementGroups)
        );
      }
    }
    return groups;
  }

  createProcurementGroup(
    orderNumber: string,
    group: {
      procurementType: ProcurementType;
      vendorNumber: string;
      orderItems: any[];
    },
    procurementGroups: ProcurementGroup[]
  ): ProcurementGroup {
    return {
      orderNumber,
      procurementType: group.procurementType,
      vendorNumber: group.vendorNumber,
      orderItems: group.orderItems,
      goodSuppliers:
        procurementGroups.find(
          (i) =>
            i.procurementType === group.procurementType &&
            i?.goodSuppliers.every(
              (e) => +e?.vendorNumber === +group.vendorNumber
            )
        )?.goodSuppliers ?? [],
    };
  }

  toBase(m: RequestOrderItem) {
    return ({
      ...m,
      partNumber: m.materialNumber,
      checked: false,
      hotFlagCode: m.hotflag,
      isHotFlag: true,
      quantity: m.orderQuantity,
      finalPrice: m.unitPrice ?? m.itemTotal,
      roItemno: m.roItemno,
    });
  }


  getVendorNumber(vendorNumber?: string): string {
    const vendorNumberValue = vendorNumber ?? "";
    return vendorNumberValue.padStart(10, "0");
  }

  private getPurchaseRequisitionApprovalMessage(error: unknown): string | undefined {
    return this.getErrorMessages(error)
      .find((message) => message.includes(purchaseRequisitionApprovalMessage));
  }

  private getErrorMessages(error: unknown): string[] {
    if (Array.isArray(error)) {
      return error.flatMap((item) => this.getErrorMessages(item));
    }

    if (typeof error === 'string') {
      return [error];
    }

    if (error && typeof error === 'object') {
      const messageResponse = error as MessageResponse;
      const messages = [
        messageResponse.message,
        messageResponse.Message,
        messageResponse.errorMessage,
        messageResponse.ErrorMessage,
        ...this.getErrorMessages(messageResponse.messages),
        ...this.getErrorMessages(messageResponse.Messages)
      ];

      return messages.filter((message): message is string => typeof message === 'string');
    }

    return [];
  }

  private createPurchaseRequisitionApprovalResult(message: string): CreatePurchaseOrdersBatchResult {
    return {
      items: [{
        success: true,
        messages: [{ message, level: 'success', type: 'success' }],
        purchaseOrderNumber: '',
        documentType: 'PR'
      }],
      filename: '',
      blob: new Blob()
    };
  }
}
